import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import ActivityFormFields from './ActivityFormFields';
import { catchErrorMessage } from 'utility/helpers';
import { toast } from 'react-toastify';
import { ProjectsAPI } from '../../apis';
import useUserData from 'hooks/useUserData';
import { useParams } from 'react-router-dom';
import { useActivityLogQuery } from '../../queries';
import { CommentType } from '@buildwise/pages/article/hooks/useArticleDetailsQuery';

export type ActivityFormValues = {
  objectSubType: string;
  objectDescription: string;
  attachments: AttachmentType[];
};

const ActivityFormValidations = Yup.object().shape({});

type ActivityFormProps = {
  log?: CommentType;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function ActivityForm({ log, setOpen }: ActivityFormProps) {
  const { projectId = '' } = useParams();
  const { companyId, user } = useUserData();
  const activityForm = useForm<ActivityFormValues>({
    resolver: yupResolver(ActivityFormValidations),
    defaultValues: {
      objectSubType: log?.objectSubType || '',
      objectDescription: log?.objectDescription || '',
      attachments: log?.attachments || [],
    },
  });
  const {
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = activityForm;
  const { refetch } = useActivityLogQuery({ companyId, objectId: projectId, shouldFetch: false });

  const handleAdd = useCallback(
    async (values: ActivityFormValues) => {
      try {
        await ProjectsAPI.addActivityLog({
          companyId,
          objectName: 'PROJECT',
          comment: {
            ...(log && {
              id: log.id,
            }),
            commentAuthor: user?.username || '',
            objectDescription: values.objectDescription,
            objectId: projectId,
            objectName: 'PROJECT',
            objectType: 'ActivityLog',
            objectSubType: values.objectSubType,
          },
        });
        reset();
        refetch();
        setOpen(false);
      } catch (error) {
        console.log('ADD_ACTIVITY_ERROR', error);
        toast.error(catchErrorMessage(error));
      }
    },
    [user, projectId],
  );
  const onAdd = handleSubmit(handleAdd);

  return (
    <Box my={1}>
      <Typography
        sx={(theme) => ({
          color: theme.palette.custom.tuna,
          fontSize: 14,
          fontWeight: 700,
          mb: 1.5,
        })}
      >
        {log ? 'Edit Activity' : 'New Activity'}
      </Typography>
      <FormProvider {...activityForm}>
        <ActivityFormFields />
      </FormProvider>
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} my={1.5}>
        <Button
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" disabled={isSubmitting} onClick={onAdd}>
          {isSubmitting ? (
            <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
          ) : log ? (
            'Save'
          ) : (
            'Add'
          )}
        </Button>
      </Box>
      <Divider />
    </Box>
  );
}

export default ActivityForm;
